// CB:BEGIN admin-edit-contractor-dashboard
import React, { useState, useEffect } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SharedHeaderAdmin from '../../components/SharedHeaderAdmin';
import SettingsCard from '../../components/ui/SettingsCard';
import { useApi } from '@/lib/useApi';

interface AdminEditContractorDashboardProps {
  contractorId: string;
  onNavigate?: (screen: string) => void;
}

interface Contractor {
  id: string;
  company: string;
  firstName: string;
  lastName: string;
  businessAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  licenseNumber?: string;
  businessName?: string;
  yearsInBusiness?: number;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceExpiry?: string;
  serviceAreas: string[];
  specialties: string[];
  verified: boolean;
  bidFeeOverride?: 'DEFAULT' | 'NO_BID_FEE' | 'BID_FEE';
  user: {
    email: string;
    phone?: string;
  };
  bids: Bid[];
}

interface Bid {
  id: string;
  projectId: string;
  amount?: number;
  amountCents?: number;
  note?: string;
  status: string;
  isAccepted: boolean;
  isFake: boolean;
  contractorName?: string;
  contractorEmail?: string;
  contractorPhone?: string;
  createdAt: string;
  project: Project;
}

interface Project {
  id: string;
  address: string;
  status: string;
  projectType: string;
  desiredStart?: string;
  roofScopeOnly: boolean;
  isPartial: boolean;
  extraInfo?: string;
  acceptedContractorName?: string;
  acceptedBidCost?: number;
  createdAt: string;
  updatedAt: string;
  homeowner: {
    id: string;
    firstName?: string;
    lastName?: string;
    address?: string;
    user: {
      email: string;
      phone?: string;
    };
  };
  bids: Bid[];
  manualBids: ManualBid[];
  phases: ProjectPhase[];
}

interface ManualBid {
  id: string;
  amountCents: number;
  contractorName: string;
  contractorEmail: string;
  contractorPhone?: string;
  note?: string;
  createdAt: string;
}

interface ProjectPhase {
  id: string;
  number: number;
  title?: string;
  status: string;
  percentOfTotal?: number;
  amountCents?: number;
  contractorSubmittedAt?: string;
  homeownerAcceptedAt?: string;
  depositReleasedAt?: string;
  completedAt?: string;
}

const AdminEditContractorDashboard: React.FC<AdminEditContractorDashboardProps> = ({ 
  contractorId, 
  onNavigate 
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectFilter, setProjectFilter] = useState<'all' | 'bidding' | 'in_progress' | 'completed'>('all');

  // Project form state (dashboard-relevant)
  const [projectStatus, setProjectStatus] = useState('');
  const [projectType, setProjectType] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [desiredStart, setDesiredStart] = useState('');
  const [roofScopeOnly, setRoofScopeOnly] = useState(false);
  const [isPartial, setIsPartial] = useState(false);
  const [extraInfo, setExtraInfo] = useState('');
  const [funding, setFunding] = useState('');
  const [textOptIn, setTextOptIn] = useState(false);

  // Bid state
  const [bids, setBids] = useState<Bid[]>([]);
  const [manualBids, setManualBids] = useState<ManualBid[]>([]);

  // Individual bid editing state
  const [editingBid, setEditingBid] = useState<string | null>(null);
  const [bidAmountCents, setBidAmountCents] = useState('');
  const [bidNote, setBidNote] = useState('');
  const [bidStatus, setBidStatus] = useState('');
  const [bidIsAccepted, setBidIsAccepted] = useState(false);
  const [bidIsFake, setBidIsFake] = useState(false);
  const [bidContractorName, setBidContractorName] = useState('');
  const [bidContractorEmail, setBidContractorEmail] = useState('');
  const [bidContractorPhone, setBidContractorPhone] = useState('');

  useEffect(() => {
    fetchContractorData();
  }, [contractorId]);

  const fetchContractorData = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/admin/contractors/${contractorId}`);
      
      setContractor(data);

      // Set bids (dashboard-relevant)
      setBids(data.bids || []);
    } catch (err) {
      console.error('Error fetching contractor data:', err);
      setError('Failed to load contractor data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectData = async (projectId: string) => {
    console.log(`🔍 Fetching project ${projectId} for contractor...`);
    try {
      const data = await api.get(`/api/admin/projects/${projectId}`);
      console.log(`✅ Project data received:`, data);
      setSelectedProject(data);
      
      // Set project form values
      setProjectStatus(data.status || '');
      setProjectType(data.projectType || '');
      setAddress(data.address || '');
      setCity(data.city || '');
      setState(data.state || '');
      setZip(data.zip || '');
      setDesiredStart(data.desiredStart ? data.desiredStart.split('T')[0] : '');
      setRoofScopeOnly(data.roofScopeOnly || false);
      setIsPartial(data.isPartial || false);
      setExtraInfo(data.extraInfo || '');
      setFunding(data.funding || '');
      setTextOptIn(data.textOptIn || false);
      
      setManualBids(data.manualBids || []);
    } catch (err) {
      console.error('Error fetching project data:', err);
      setError('Failed to load project data');
    }
  };

  const handleSaveProject = async () => {
    if (!selectedProject) return;

    try {
      setSaving(true);
      setError(null);

      await api.put(`/api/admin/projects/${selectedProject.id}`, {
        status: projectStatus,
        projectType,
        address,
        city,
        state,
        zip,
        desiredStart: desiredStart ? new Date(desiredStart).toISOString() : null,
        roofScopeOnly,
        isPartial,
        extraInfo,
        funding,
        textOptIn
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchProjectData(selectedProject.id); // Refresh project data
      }, 2000);
    } catch (err) {
      console.error('Error saving project data:', err);
      setError('Failed to save project changes');
    } finally {
      setSaving(false);
    }
  };

  const getProjectBids = (projectId: string) => {
    return bids.filter(bid => bid.projectId === projectId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return '#6b7280';
      case 'BIDDING': return '#f59e0b';
      case 'BID_SELECTED': return '#10b981';
      case 'SCHEDULED': return '#3b82f6';
      case 'IN_PROGRESS': return '#8b5cf6';
      case 'COMPLETED': return '#059669';
      case 'CANCELLED': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const handleEditBid = (bid: Bid) => {
    setEditingBid(bid.id);
    setBidAmountCents(bid.amountCents ? (bid.amountCents / 100).toString() : '');
    setBidNote(bid.note || '');
    setBidStatus(bid.status || '');
    setBidIsAccepted(bid.isAccepted);
    setBidIsFake(bid.isFake);
    setBidContractorName(bid.contractorName || '');
    setBidContractorEmail(bid.contractorEmail || '');
    setBidContractorPhone(bid.contractorPhone || '');
  };

  const handleSaveBid = async (bidId: string) => {
    try {
      setSaving(true);
      setError(null);

      await api.put(`/api/admin/bids/${bidId}`, {
        amountCents: bidAmountCents ? Math.round(parseFloat(bidAmountCents) * 100) : null,
        note: bidNote,
        status: bidStatus,
        isAccepted: bidIsAccepted,
        isFake: bidIsFake,
        contractorName: bidContractorName,
        contractorEmail: bidContractorEmail,
        contractorPhone: bidContractorPhone
      });

      setEditingBid(null);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchProjectData(selectedProject!.id); // Refresh project data
      }, 2000);
    } catch (err) {
      console.error('Error saving bid:', err);
      setError('Failed to save bid changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditBid = () => {
    setEditingBid(null);
    setBidAmountCents('');
    setBidNote('');
    setBidStatus('');
    setBidIsAccepted(false);
    setBidIsFake(false);
    setBidContractorName('');
    setBidContractorEmail('');
    setBidContractorPhone('');
  };

  if (loading) {
    return (
      <ResponsiveContainer className="container">
        <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p style={{ marginTop: 16, color: '#6b7280' }}>Loading contractor data...</p>
        </div>
      </ResponsiveContainer>
    );
  }

  if (!contractor) {
    return (
      <ResponsiveContainer className="container">
        <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ color: '#dc2626' }}>Contractor not found</p>
        </div>
      </ResponsiveContainer>
    );
  }

  // Get unique projects from bids, filtering out any null projects or homeowners
  const projects = bids
    .map(bid => bid.project)
    .filter((project): project is Project => 
      project !== null && 
      project !== undefined && 
      project.homeowner !== null && 
      project.homeowner !== undefined
    )
    .filter((project, index, self) => 
      index === self.findIndex(p => p.id === project.id)
    );
  
  console.log(`🔍 Projects for contractor: ${projects.length}`, projects);
  console.log(`🔍 Selected project: ${selectedProject?.id || 'none'}`);

  return (
    <ResponsiveContainer className="container">
      <div className="content">
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => onNavigate?.('admin-contractors-list')}
              style={{ fontSize: 14, padding: '8px 16px' }}
            >
              ← Back to Contractors
            </button>
            <h1 style={{ fontSize: 28, margin: 0 }}>
              Edit Dashboard - {contractor.company}
            </h1>
          </div>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            Edit project information and manage bids that affect the contractor's dashboard display
          </p>
        </div>

        {error && (
          <div style={{ 
            background: '#fee2e2', 
            border: '1px solid #fecaca', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 24,
            color: '#dc2626'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div style={{ 
            background: '#d1fae5', 
            border: '1px solid #a7f3d0', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 24,
            color: '#065f46'
          }}>
            <strong>Success:</strong> Changes saved successfully!
          </div>
        )}

        {/* Projects List */}
        <SettingsCard title={`Projects (${projects.length})`}>
          {projects.length > 0 ? (
            <div style={{ display: 'grid', gap: 12 }}>
              {projects.map((project) => {
                const projectBids = getProjectBids(project.id);
                const acceptedBid = projectBids.find(bid => bid.isAccepted);
                
                return (
                  <div 
                    key={project.id} 
                    style={{ 
                      border: '1px solid #e5e7eb', 
                      borderRadius: 8, 
                      padding: 16,
                      cursor: 'pointer',
                      background: selectedProject?.id === project.id ? '#f0f9ff' : '#ffffff',
                      borderColor: selectedProject?.id === project.id ? '#3b82f6' : '#e5e7eb'
                    }}
                    onClick={() => fetchProjectData(project.id)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <h4 style={{ fontSize: 16, fontWeight: '600', margin: 0 }}>
                          {project.projectType} - {project.address}
                        </h4>
                        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                          Homeowner: {project.homeowner?.firstName || 'N/A'} {project.homeowner?.lastName || ''}
                        </p>
                        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                          Email: {project.homeowner?.user?.email || 'N/A'}
                        </p>
                        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                          Bids: {projectBids.length} | Status: {project.status}
                        </p>
                        {acceptedBid && (
                          <p style={{ fontSize: 14, color: '#059669', margin: '4px 0', fontWeight: '600' }}>
                            ✓ Accepted Bid: ${acceptedBid.amountCents ? (acceptedBid.amountCents / 100).toFixed(2) : 'N/A'}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontSize: 12,
                          padding: '4px 8px',
                          borderRadius: 4,
                          background: '#f3f4f6',
                          color: getStatusColor(project.status)
                        }}>
                          {project.status}
                        </span>
                        <span style={{
                          fontSize: 12,
                          padding: '4px 8px',
                          borderRadius: 4,
                          background: '#e0f2fe',
                          color: '#0369a1'
                        }}>
                          {projectBids.length} bids
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
              No projects found for this contractor
            </p>
          )}
        </SettingsCard>

        {/* Selected Project Details */}
        {selectedProject && (
          <>
            {/* Project Information */}
            <SettingsCard title={`Project Information - ${selectedProject.projectType} - ${selectedProject.address}`}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Project Status
                  </label>
                  <select
                    value={projectStatus}
                    onChange={(e) => setProjectStatus(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="BIDDING">Bidding</option>
                    <option value="BID_SELECTED">Bid Selected</option>
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Project Type
                  </label>
                  <select
                    value={projectType}
                    onChange={(e) => setProjectType(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  >
                    <option value="ROOF">Roof Replacement</option>
                    <option value="GUTTER">Gutter Installation</option>
                    <option value="SIDING">Siding</option>
                    <option value="WINDOWS">Windows</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Address
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Desired Start Date
                  </label>
                  <input
                    type="date"
                    value={desiredStart}
                    onChange={(e) => setDesiredStart(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Funding
                  </label>
                  <input
                    type="text"
                    value={funding}
                    onChange={(e) => setFunding(e.target.value)}
                    className="form-control"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Additional Project Information
                  </label>
                  <textarea
                    value={extraInfo}
                    onChange={(e) => setExtraInfo(e.target.value)}
                    className="form-control"
                    rows={3}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 20, marginTop: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={roofScopeOnly}
                    onChange={(e) => setRoofScopeOnly(e.target.checked)}
                  />
                  <span style={{ fontSize: 14, color: '#374151' }}>Roof scope only</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={isPartial}
                    onChange={(e) => setIsPartial(e.target.checked)}
                  />
                  <span style={{ fontSize: 14, color: '#374151' }}>Partial project</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={textOptIn}
                    onChange={(e) => setTextOptIn(e.target.checked)}
                  />
                  <span style={{ fontSize: 14, color: '#374151' }}>Text opt-in</span>
                </label>
              </div>
            </SettingsCard>

            {/* Homeowner Information */}
            <SettingsCard title={`Homeowner Information - ${selectedProject.homeowner?.firstName} ${selectedProject.homeowner?.lastName}`}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={selectedProject.homeowner?.firstName || ''}
                    readOnly
                    className="form-control"
                    style={{ width: '100%', backgroundColor: '#f9fafb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={selectedProject.homeowner?.lastName || ''}
                    readOnly
                    className="form-control"
                    style={{ width: '100%', backgroundColor: '#f9fafb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={selectedProject.homeowner?.user?.email || ''}
                    readOnly
                    className="form-control"
                    style={{ width: '100%', backgroundColor: '#f9fafb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={selectedProject.homeowner?.user?.phone || ''}
                    readOnly
                    className="form-control"
                    style={{ width: '100%', backgroundColor: '#f9fafb' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                    Address
                  </label>
                  <input
                    type="text"
                    value={selectedProject.homeowner?.address || ''}
                    readOnly
                    className="form-control"
                    style={{ width: '100%', backgroundColor: '#f9fafb' }}
                  />
                </div>
              </div>
            </SettingsCard>
          </>
        )}

        {/* Contractor's Bids for Selected Project */}
        {selectedProject && (
          <SettingsCard title={`Contractor's Bids for ${selectedProject.address}`}>
            {(() => {
              const projectBids = getProjectBids(selectedProject.id);
              return projectBids.length > 0 ? (
                <div style={{ display: 'grid', gap: 16 }}>
                  {projectBids.map((bid) => (
                    <div key={bid.id} style={{ 
                      border: '1px solid #e5e7eb', 
                      borderRadius: 8, 
                      padding: 16,
                      background: bid.isAccepted ? '#f0fdf4' : '#ffffff'
                    }}>
                      {editingBid === bid.id ? (
                        // Edit Mode
                        <div style={{ display: 'grid', gap: 12 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                              <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                                Contractor Name
                              </label>
                              <input
                                type="text"
                                value={bidContractorName}
                                onChange={(e) => setBidContractorName(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                                Amount ($)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={bidAmountCents}
                                onChange={(e) => setBidAmountCents(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                          </div>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                              <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                                Contractor Email
                              </label>
                              <input
                                type="email"
                                value={bidContractorEmail}
                                onChange={(e) => setBidContractorEmail(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                                Contractor Phone
                              </label>
                              <input
                                type="tel"
                                value={bidContractorPhone}
                                onChange={(e) => setBidContractorPhone(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                              Status
                            </label>
                            <select
                              value={bidStatus}
                              onChange={(e) => setBidStatus(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="">Select Status</option>
                              <option value="PROPOSED">Proposed</option>
                              <option value="ACCEPTED">Accepted</option>
                              <option value="REJECTED">Rejected</option>
                              <option value="WITHDRAWN">Withdrawn</option>
                            </select>
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: '500', marginBottom: 4 }}>
                              Note
                            </label>
                            <textarea
                              value={bidNote}
                              onChange={(e) => setBidNote(e.target.value)}
                              rows={3}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                resize: 'vertical'
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input
                                type="checkbox"
                                checked={bidIsAccepted}
                                onChange={(e) => setBidIsAccepted(e.target.checked)}
                              />
                              <span style={{ fontSize: 14 }}>Accepted</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input
                                type="checkbox"
                                checked={bidIsFake}
                                onChange={(e) => setBidIsFake(e.target.checked)}
                              />
                              <span style={{ fontSize: 14 }}>Fake Bid</span>
                            </label>
                          </div>

                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              onClick={handleCancelEditBid}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#f3f4f6',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSaveBid(bid.id)}
                              disabled={saving}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                opacity: saving ? 0.6 : 1
                              }}
                            >
                              {saving ? 'Saving...' : 'Save Bid'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ fontSize: 16, fontWeight: '600', margin: 0 }}>
                                {bid.contractorName || 'Unknown Contractor'}
                              </h4>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                                <div>
                                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Email:</p>
                                  <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                    {bid.contractorEmail || 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Phone:</p>
                                  <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                    {bid.contractorPhone || 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Amount:</p>
                                  <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                    ${bid.amountCents ? (bid.amountCents / 100).toFixed(2) : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Status:</p>
                                  <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                    {bid.status || 'N/A'}
                                  </p>
                                </div>
                              </div>
                              {bid.note && (
                                <div style={{ marginTop: 8 }}>
                                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Note:</p>
                                  <p style={{ fontSize: 14, color: '#374151', margin: '2px 0' }}>
                                    {bid.note}
                                  </p>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                {bid.isAccepted && (
                                  <span style={{ 
                                    fontSize: 12, 
                                    padding: '2px 6px', 
                                    backgroundColor: '#10b981', 
                                    color: 'white', 
                                    borderRadius: '4px' 
                                  }}>
                                    ACCEPTED
                                  </span>
                                )}
                                {bid.isFake && (
                                  <span style={{ 
                                    fontSize: 12, 
                                    padding: '2px 6px', 
                                    backgroundColor: '#f59e0b', 
                                    color: 'white', 
                                    borderRadius: '4px' 
                                  }}>
                                    FAKE
                                  </span>
                                )}
                              </div>
                              <p style={{ fontSize: 12, color: '#9ca3af', margin: '8px 0 0 0' }}>
                                Created: {new Date(bid.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => handleEditBid(bid)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                  No bids found for this project
                </p>
              );
            })()}
          </SettingsCard>
        )}

        {/* Manual Bids for Selected Project */}
        {selectedProject && manualBids.length > 0 && (
          <SettingsCard title={`Manual Bids for ${selectedProject.address}`}>
            <div style={{ display: 'grid', gap: 16 }}>
              {manualBids.map((bid) => (
                <div key={bid.id} style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  padding: 16,
                  background: '#f9fafb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <h4 style={{ fontSize: 16, fontWeight: '600', margin: 0 }}>
                        {bid.contractorName}
                      </h4>
                      <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                        {bid.contractorEmail}
                      </p>
                      <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                        Amount: ${(bid.amountCents / 100).toFixed(2)}
                      </p>
                      {bid.note && (
                        <p style={{ fontSize: 14, color: '#374151', margin: '4px 0' }}>
                          Note: {bid.note}
                        </p>
                      )}
                    </div>
                    <span style={{
                      fontSize: 12,
                      padding: '4px 8px',
                      borderRadius: 4,
                      background: '#fef3c7',
                      color: '#92400e'
                    }}>
                      Manual Bid
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </SettingsCard>
        )}

        {/* Project Phases for Selected Project */}
        {selectedProject && selectedProject.phases && selectedProject.phases.length > 0 && (
          <SettingsCard title={`Project Phases for ${selectedProject.address}`}>
            <div style={{ display: 'grid', gap: 16 }}>
              {selectedProject.phases.map((phase) => (
                <div key={phase.id} style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  padding: 16,
                  background: '#ffffff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <h4 style={{ fontSize: 16, fontWeight: '600', margin: 0 }}>
                        Phase {phase.number}: {phase.title || 'Untitled Phase'}
                      </h4>
                      <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                        Status: {phase.status}
                      </p>
                      {phase.percentOfTotal && (
                        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                          Percent of Total: {phase.percentOfTotal}%
                        </p>
                      )}
                      {phase.amountCents && (
                        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0' }}>
                          Amount: ${(phase.amountCents / 100).toFixed(2)}
                        </p>
                      )}
                    </div>
                    <span style={{
                      fontSize: 12,
                      padding: '4px 8px',
                      borderRadius: 4,
                      background: '#e0f2fe',
                      color: '#0369a1'
                    }}>
                      Phase {phase.number}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </SettingsCard>
        )}

        {/* Save Button for Selected Project */}
        {selectedProject && (
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 32 }}>
            <button
              onClick={handleSaveProject}
              disabled={saving}
              className="btn btn-primary"
              style={{ 
                fontSize: 16, 
                padding: '12px 32px',
                background: '#059669',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Project Changes'}
            </button>
          </div>
        )}
      </div>
    </ResponsiveContainer>
  );
};

export default AdminEditContractorDashboard;
// CB:END admin-edit-contractor-dashboard
